import ReactGA from "react-ga4";
import { stripUndefined } from "../js_utils/object_utils";
import {
  Analytics,
  EcommerceEvent,
  ProductProperties,
  TrackProperties,
} from "./types";

export class GA4SourceProvider {
  trackers: GA4Client[] = [];

  preload(tracker: GA4Client) {
    const existing = this.trackers.find(
      (t) => t.trackingId === tracker.trackingId
    );
    if (!existing) {
      this.trackers.push(tracker);
    }

    return GA4Analytics(this.trackers);
  }

  initialize(tracker: GA4Client) {
    const existing = this.trackers.find(
      (t) => t.trackingId === tracker.trackingId
    );
    if (!existing) {
      if (this.trackers.length > 0) {
        ReactGA.reset();
      }

      ReactGA.initialize([...this.trackers, tracker]);

      this.trackers.push(tracker);
    }

    return GA4Analytics(this.trackers);
  }
}

interface GA4Tracker {
  trackingId: string;
}

interface GA4TrackerOptions {
  trackingId: string;
  gaOptions: {
    name: string;
    storage?: string;
    clientId?: string;
    cookieDomain?: string;
  };
}

export interface GA4Client extends GA4TrackerOptions {
  provider: "ga4";
}

export function makeGA4Client(tracker: GA4TrackerOptions): GA4Client {
  return {
    provider: "ga4",
    trackingId: tracker.trackingId,
    gaOptions: {
      ...tracker.gaOptions,
      cookieDomain: "auto",
    },
  };
}

function GA4Analytics(trackers: GA4Tracker[]): Analytics {
  return {
    group: () => {},
    register: () => {},
    identify: () => {},
    screen: (name) => {
      ReactGA._gaCommandSendPageview(window.location.pathname, {
        hitType: "pageview",
        ...(name && { title: name }),
      });
    },
    ecommerceEvent: (event) => {
      sendGA4EcommerceEvent(event);
    },
    event: (eventName: string, properties: TrackProperties) => {
      ReactGA.event(eventName, {
        category: properties.category,
        label: properties.label,
        ...properties,
      });
    },
  };
}

function sendGA4EcommerceEvent(event: EcommerceEvent) {
  switch (event.event) {
    case "Product Viewed":
      // Default GA4 event
      ReactGA.event(
        `view_item`,
        stripUndefined({
          currency: event.currency,
          value: event.revenue,
          items: event.products.map(toProductItem),
        })
      );
      break;
    case "Checkout Started":
      ReactGA.event("Checkout Started", {
        value: event.revenue,
        currency: event.currency,
      });
      // Default GA4 event
      ReactGA.event(
        "add_to_cart",
        stripUndefined({
          currency: event.currency,
          value: event.revenue,
          items: event.products.map(toProductItem),
        })
      );
      ReactGA.event(
        "begin_checkout",
        stripUndefined({
          currency: event.currency,
          value: event.revenue,
          coupon: event.coupon,
          items: event.products.map(toProductItem),
        })
      );
      break;
    case "Order Completed":
      ReactGA.event("Order Completed", {
        value: event.revenue,
        currency: event.currency,
      });

      // Default GA4 event
      ReactGA.event(
        "purchase",
        stripUndefined({
          id: event.id,
          transaction_id: event.affiliation,
          currency: event.currency,
          value: event.revenue,
          tax: event.tax,
          shipping: event.shipping,
          coupon: event.coupon,
          items: event.products.map(toProductItem),
        })
      );
      break;
    case "Promotion Viewed":
      ReactGA.event("view_promotion", {
        items: [
          {
            item_id: event.id,
            coupon: event.name,
            creative_name: event.creative,
          },
        ],
      });
      break;
    case "Promotion Clicked":
      ReactGA.event("select_promotion", {
        items: [
          {
            item_id: event.id,
            coupon: event.name,
            creative_name: event.creative,
          },
        ],
      });
      break;
    default:
      console.error(`Analytics: event ${event.event} not supported yet.`);
      break;
  }
}

function toProductItem(item: ProductProperties) {
  return {
    item_id: item.id,
    item_name: item.name,
    item_brand: item.brand,
    item_category: item.category,
    item_category2: item.category2,
    item_category3: item.category3,
    item_category4: item.category4,
    item_category5: item.category5,
    item_variant: item.variant,
    quantity: item.quantity,
    price: item.price,
    coupon: item.coupon,
    discount: item.discount,
  };
}
